import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase-config";
import { sendEmailVerification } from "firebase/auth";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, addDoc, collection } from "firebase/firestore";
import { db } from "../firebase-config";
import ButtonComponent from "../components/ui/ButtonComponent";
import "../style.css";

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [unverifiedUser, setUnverifiedUser] = useState(null);

  const persistSessionFromUserDoc = (user, userData) => {
    const rolesArray = Array.isArray(userData?.roles)
      ? userData.roles
      : (typeof userData?.role === "string" && userData.role ? [userData.role] : []);

    sessionStorage.setItem("organizationId", userData.organizationId || "");
    sessionStorage.setItem("userRole", rolesArray.join(",")); // ✅ consistent
    localStorage.setItem("userEmail", user.email || "");
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && user.emailVerified) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            persistSessionFromUserDoc(user, userData);

            console.log("User session restored:", user.email, "Org:", userData.organizationId, "Roles:", userData.roles);

            // ✅ always go through post-login decision logic
            navigate("/post-login", { replace: true });
          }
        } catch (err) {
          console.error("Error fetching user data:", err);
        }
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setUnverifiedUser(null);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        setUnverifiedUser(user);
        throw new Error("Please verify your email before logging in.");
      }

      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error("User not found. Contact your org admin.");
      }

      const userData = userDoc.data();
      persistSessionFromUserDoc(user, userData);

      const rolesArray = Array.isArray(userData?.roles) ? userData.roles : [];
      console.log("Logged in as:", user.email, "Org:", userData.organizationId, "Roles:", rolesArray);

      // Log Active Sessions
      const sessionId = `${user.uid}_${new Date().getTime()}`;
      const userSessionRef = doc(db, "activeSessions", sessionId);

      await setDoc(userSessionRef, {
        userId: user.uid,
        email: user.email,
        loginTime: serverTimestamp(),
        organizationId: userData.organizationId,
      });

      sessionStorage.setItem("sessionId", sessionId);

      // Log to loginTrends
      await addDoc(collection(db, "loginTrends"), {
        userId: user.uid,
        email: user.email,
        organizationId: userData.organizationId,
        timestamp: serverTimestamp(),
        success: true,
        roles: rolesArray,
      });

      // Log to audits
      await addDoc(collection(db, "audits"), {
        action: "User_Login",
        performedBy: user.uid,
        email: user.email,
        organizationId: userData.organizationId,
        timestamp: serverTimestamp(),
      });

      // ✅ always go through post-login decision logic
      navigate("/post-login", { replace: true });
    } catch (error) {
      console.error("Raw login error:", error);

      const safeMessage = error?.message || "Unknown error occurred during login";
      setError(safeMessage);

      // Log frontend system error
      await addDoc(collection(db, "systemLogs"), {
        type: "Error",
        message: safeMessage,
        email: email,
        timestamp: serverTimestamp(),
      });

      // Log backend + trends (existing behavior)
      await logFailedLoginAttempt(email, safeMessage);

      await addDoc(collection(db, "loginTrends"), {
        email: email,
        organizationId: "unknown",
        timestamp: serverTimestamp(),
        success: false,
        roles: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const logFailedLoginAttempt = async (email, errorMessage) => {
    try {
      await fetch("https://logfailedlogin-774073159618.us-central1.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, errorMessage }),
      });

      await addDoc(collection(db, "loginTrends"), {
        email: email,
        organizationId: "unknown",
        timestamp: serverTimestamp(),
        success: false,
        role: "unknown",
      });

      console.log("Failed login attempt logged for:", email);
    } catch (error) {
      console.error("Error logging failed login attempt:", error);
    }
  };

  const handlePasswordReset = async () => {
    const emailPrompt = window.prompt("Enter your email address to reset your password:");
    if (!emailPrompt) return;

    try {
      await sendPasswordResetEmail(auth, emailPrompt);
      alert("A password reset email has been sent to your inbox.");
    } catch (error) {
      console.error("Error resetting password:", error.message);
      alert("Error: " + error.message);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedUser) return;

    try {
      await sendEmailVerification(unverifiedUser);
      alert("A verification email has been resent to your email address.");
    } catch (error) {
      console.error("Error resending verification email:", error.message);
      alert("Error: " + error.message);
    }
  };

  return (
    <div className="container">
      <div className="logo-container">
        <img src="https://storage.googleapis.com/kinova/Kinova_transparent.png" alt="Kinova Logo" />
      </div>
      <div className="login-card">
        <h2>Welcome back</h2>
        <p className="subtext">Please enter your details</p>

        {error && <p className="error-message">{error}</p>}

        {unverifiedUser && (
          <p className="error-message">
            <button onClick={handleResendVerification} className="resend-button">
              Resend Verification Email
            </button>
          </p>
        )}

        <form onSubmit={handleLogin}>
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <div className="options">
            <a href="#" onClick={handlePasswordReset}>
              Forgot password?
            </a>
          </div>

          <ButtonComponent type="primary" label="Sign in" isLoading={loading} disabled={loading} />
        </form>
      </div>
    </div>
  );
}

export default Login;